"use client";

import { faArrowRightFromBracket } from "@fortawesome/pro-solid-svg-icons/faArrowRightFromBracket";
import { faBookmark } from "@fortawesome/pro-solid-svg-icons/faBookmark";
import { faChartLine } from "@fortawesome/pro-solid-svg-icons/faChartLine";
import { faComment } from "@fortawesome/pro-solid-svg-icons/faComment";
import { faFileArrowUp } from "@fortawesome/pro-solid-svg-icons/faFileArrowUp";
import { faFlask } from "@fortawesome/pro-solid-svg-icons/faFlask";
import { faGear } from "@fortawesome/pro-solid-svg-icons/faGear";
import { faRss } from "@fortawesome/pro-solid-svg-icons/faRss";
import { faScrewdriverWrench } from "@fortawesome/pro-solid-svg-icons/faScrewdriverWrench";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Flex,
  Image,
  Text,
  Tooltip,
  Transition,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { AccountModal } from "@/components/account-modal";
import CsvModal from "@/components/csvModal/csvModal";
import { UserManagementModal } from "@/components/header/user-management-modal";
import { useUserContext } from "@/context/user.context";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { useAtsSuiteAccess } from "@/hooks/use-ats-suite-access.hook";
import { useJobFeedAccess } from "@/hooks/use-job-feed-access.hook";
import { useAdminOnlyRender } from "@/hooks/use-role-limited-render";
import { closeExtension } from "@/utils/extension";
import { LAYOUT_DIMENSIONS } from "@/utils/ui";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { UniversalSearch } from "../universal-search";

const getSharedButtonProps = (isCurrentLink = false) => ({
  variant: isCurrentLink ? "filled" : "transparent",
  fw: 600,
  px: 12,
  size: "xs",
  bg: isCurrentLink ? "#EFEAFA" : "transparent",
  c: "purple.5",
  styles: {
    inner: {
      fontSize: 14,
      fontWeight: 700,
    },
    root: {
      borderRadius: 20,
      height: 32,
    },
  },
});

const getSharedActionIconProps = () => ({
  variant: "filled",
  size: "md",
  bg: "gray.0",
  c: "gray.7",
  styles: {
    root: {
      borderRadius: 20,
      height: 32,
      width: 32,
    },
  },
});

interface HeaderProps {
  hideAccountButtons?: boolean;
}

export const Header = ({ hideAccountButtons = false }: HeaderProps) => {
  const track = useTrackAnalytics();
  const pathname = usePathname();
  const { isInternal, logOut, user } = useUserContext();
  const [
    accountModalOpened,
    { close: closeAccountModal, open: openAccountModal },
  ] = useDisclosure(false);
  const [usersModalOpened, { close: closeUsersModal, open: openUsersModal }] =
    useDisclosure(false);
  const [universalSearchOpened, { toggle: toggleUniversalSearch }] =
    useDisclosure(false);
  const [csvModalOpened, { open: openCsvModal, close: closeCsvModal }] =
    useDisclosure(false);
  const adminOnlyRender = useAdminOnlyRender();
  const { hasAccess: atsSuiteAccess } = useAtsSuiteAccess();
  const { hasAccess: hasJobFeedAccess } = useJobFeedAccess();
  const hasModelBenchmark =
    isInternal && Boolean(process.env.NEXT_PUBLIC_MODEL_BENCHMARK_URL);
  const logo = useMemo(
    () => <Image src="/assets/logo.svg" alt="Logo" h={20} />,
    [],
  );

  return (
    <header
      className={twMerge([
        "bg-white",
        "border-b-2",
        "border-gray-2000",
        "flex",
        "items-center",
        "justify-between",
        "px-[18px]",
        "w-full",
        "z-40",
        "relative",
      ])}
      style={{ height: LAYOUT_DIMENSIONS.HEADER_HEIGHT_PX }}
    >
      <div className="flex gap-6 items-center">
        {user ? <Link href="/">{logo}</Link> : logo}
        <Text size="sm" fw={600}>
          {user?.company.name || ""}
        </Text>
      </div>
      <Flex align="center" gap={8}>
        {user && (
          <>
            {!hideAccountButtons && (
              <>
                {user.isInternal && (
                  <Button
                    {...getSharedButtonProps()}
                    onClick={openCsvModal}
                    rightSection={<FontAwesomeIcon icon={faFileArrowUp} />}
                  >
                    Upload CSV
                  </Button>
                )}
                <Button
                  {...getSharedButtonProps(
                    pathname === "/" ||
                      pathname.startsWith("/screener") ||
                      pathname.startsWith("/screener-location"),
                  )}
                  component={Link}
                  href="/"
                  rightSection={<FontAwesomeIcon icon={faComment} />}
                >
                  Interviews
                </Button>
                <Button
                  {...getSharedButtonProps(pathname === "/assigned-to/me")}
                  component={Link}
                  href="/assigned-to/me"
                  rightSection={<FontAwesomeIcon icon={faBookmark} />}
                >
                  Assigned
                </Button>
                <Button
                  {...getSharedButtonProps(pathname === "/analytics")}
                  component={Link}
                  href="/analytics"
                  onClick={() => track("Analytics Link Clicked")}
                  rightSection={<FontAwesomeIcon icon={faChartLine} />}
                >
                  Analytics
                </Button>
                {/* AS-2040: Job Feed tab is gated by `useJobFeedAccess` —
                    internal OR the `job-feed-tab-enabled` GrowthBook flag on
                    for this company. Internal users can also reach the page
                    via the ATS Suite tile even when the flag is off. */}
                {hasJobFeedAccess && (
                  <Button
                    {...getSharedButtonProps(pathname.startsWith("/job-feed"))}
                    component={Link}
                    href="/job-feed"
                    rightSection={<FontAwesomeIcon icon={faRss} />}
                  >
                    Job Feed
                  </Button>
                )}
                {atsSuiteAccess && (
                  <Button
                    {...getSharedButtonProps(
                      pathname.startsWith("/ats-suite") ||
                        pathname.startsWith("/integration-validator") ||
                        pathname.startsWith("/e2e-runner"),
                    )}
                    component={Link}
                    href="/ats-suite"
                    rightSection={
                      <FontAwesomeIcon icon={faScrewdriverWrench} />
                    }
                  >
                    ATS Suite
                  </Button>
                )}
                {hasModelBenchmark && (
                  <Button
                    {...getSharedButtonProps(
                      pathname.startsWith("/internal/model-benchmark"),
                    )}
                    component={Link}
                    href="/internal/model-benchmark"
                    rightSection={<FontAwesomeIcon icon={faFlask} />}
                  >
                    Model lab
                  </Button>
                )}
                {adminOnlyRender(
                  <Button
                    {...getSharedButtonProps()}
                    leftSection={<FontAwesomeIcon icon={faUser} />}
                    onClick={openUsersModal}
                  >
                    Users
                  </Button>,
                )}
                <Button
                  {...getSharedButtonProps()}
                  onClick={() => {
                    track("Account Button Clicked");
                    openAccountModal();
                  }}
                  rightSection={<FontAwesomeIcon icon={faGear} />}
                >
                  Account
                </Button>
              </>
            )}
          </>
        )}
      </Flex>
      <Flex
        align="center"
        gap={12}
        style={{
          opacity: universalSearchOpened ? 0 : 1,
          transition: "opacity 0.3s ease",
          visibility: universalSearchOpened ? "hidden" : "visible",
        }}
      >
        <Tooltip label="Search">
          <ActionIcon
            onClick={() => toggleUniversalSearch()}
            aria-label="search"
            {...getSharedActionIconProps()}
          >
            <FontAwesomeIcon icon={faSearch} size="sm" />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Log out">
          <ActionIcon
            component={Link}
            href="/auth/login?logout=true"
            onClick={() => logOut()}
            aria-label="logout"
            {...getSharedActionIconProps()}
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} size="sm" />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Close Upwage">
          <ActionIcon
            onClick={() => closeExtension()}
            aria-label="close"
            {...getSharedActionIconProps()}
          >
            <FontAwesomeIcon icon={faXmark} size="sm" />
          </ActionIcon>
        </Tooltip>
      </Flex>

      <Transition
        mounted={universalSearchOpened}
        transition="slide-left"
        duration={300}
        timingFunction="ease"
      >
        {(styles) => (
          <div
            style={{
              ...styles,
              position: "absolute",
              right: 16,
              top: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
              zIndex: 50,
            }}
          >
            <UniversalSearch close={toggleUniversalSearch} />
          </div>
        )}
      </Transition>

      <AccountModal onClose={closeAccountModal} opened={accountModalOpened} />
      {user?.isInternal && (
        <CsvModal onClose={closeCsvModal} opened={csvModalOpened} />
      )}
      {adminOnlyRender(
        <UserManagementModal
          onClose={closeUsersModal}
          opened={usersModalOpened}
        />,
      )}
    </header>
  );
};
